import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, error, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: hero image */}
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
      >
        <div className="absolute inset-0 bg-[#0A192F]/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-white flex items-center justify-center overflow-hidden">
              <img src="/csc-logo.jpg" alt="CSC" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-tight">PreventivatoreCSC</div>
              <div className="text-xs text-white/60">Centro Sicurezza Consulenze S.r.l.s.</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight max-w-lg">
              La piattaforma per offerte commerciali di consulenza sicurezza.
            </h1>
            <p className="mt-6 text-white/70 max-w-md">
              Redigi preventivi complessi in pochi minuti. Approva, invia, archivia — con precisione svizzera e stile italiano.
            </p>
            <div className="mt-10 flex gap-6 text-sm">
              <div>
                <div className="text-3xl font-display font-bold text-[#FACC15]">10</div>
                <div className="text-white/60">step guidati</div>
              </div>
              <div>
                <div className="text-3xl font-display font-bold text-[#FACC15]">9</div>
                <div className="text-white/60">stati offerta</div>
              </div>
              <div>
                <div className="text-3xl font-display font-bold text-[#FACC15]">100%</div>
                <div className="text-white/60">responsive</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              <img src="/csc-logo.jpg" alt="CSC" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">PreventivatoreCSC</div>
            </div>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#0F2C59]">Accedi al gestionale</h2>
          <p className="mt-2 text-sm text-slate-500">Utilizza le credenziali fornite dall'amministratore.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 focus:ring-[#0F2C59]"
                placeholder="nome@centrosicurezzaconsulenze.net"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#0F2C59] hover:bg-[#173F7A] text-white h-11 font-semibold shadow-sm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Accedi
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-400">
            © {new Date().getFullYear()} Centro Sicurezza Consulenze S.r.l.s. con Socio Unico · Via Bolzano 14/16, Montepulciano (SI)
          </div>
        </div>
      </div>
    </div>
  );
}
